import { createContext, useContext, type ReactNode } from "react";
import { useSeriais } from "../SeriaisContext";
import { alert } from "@/contexts/AlertModalContext";
import type { CategoriaItemResponse } from "@/types/categoria-item-response";
import type { EPCBlock } from "@/components/MinhaContaComponent";
import { useImpressaoConfig } from "../ImpressaoConfigContext";
import type { EpcItemGerado } from "@/types/epc-item-gerado";
import epcTds from "epc-tds";
import type { EmpresaSSCC } from "@/types/empresa-sscc";
import { calculateSSCCCheckDigit } from "@/utils/UtilFuncoes";
import {
  formatarValorInfoCompl,
  getTipoInfoCompl,
} from "@/components/InfoComplField";
import { useContaConfig } from "../ContaConfigContext";

export type EpcContextType = {
  gerarEpcs: (
    categoria: CategoriaItemResponse,
    quantidade: number,
    localizacoes: string[],
    blocks?: EPCBlock[],
    serialInicial?: number,
    codigoPanvel?: string,
  ) => EpcItemGerado[];
  gerarSSCC: (
    empresa: EmpresaSSCC,
    localizacoes: string[],
    quantidade?: number,
    serialInicial?: number,
  ) => EpcItemGerado[];
};

export const EpcContext = createContext<EpcContextType | undefined>(undefined);

export function EpcProvider({ children }: { children: ReactNode }) {
  const {
    impressaoConfig,
    coletarPadraoEpc,
    coletarBlocosEPC,
    coletaZplPadrao,
  } = useImpressaoConfig();
  const { seriais } = useSeriais();
  const { numeroDeImpressoes } = useContaConfig();

  const gerarIdEpc = () => {
    if (!impressaoConfig) return "";
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0"); // Mês de 0-11 para 1-12
    const day = String(now.getDate()).padStart(2, "0"); // Dia do mês de 1-31
    const formattedDateTime = `${year}${month}${day}`;
    const randomID = "xxxxxxxx-yxxx".replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0,
        v = c === "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
    return `${randomID}_${impressaoConfig?.id_conta}${formattedDateTime}`;
  };

  const gerarSSCC = (
    empresa: EmpresaSSCC,
    localizacoes: string[],
    quantidade: number = 1,
    serialInicial: number = 0,
  ) => {
    if (!empresa) {
      alert.error("Empresa SSCC não fornecida");
      return [];
    }
    if (quantidade <= 0) {
      return [];
    }
    const epcs: EpcItemGerado[] = [];
    let serialNumber = serialInicial;
    let numeroAtualImpressoes = numeroDeImpressoes || 0;

    if (seriais.length && serialInicial == 0) {
      const serial = seriais.find((s) => s.gtin === empresa.valor);
      if (serial) {
        if (serial.serial !== 0) {
          serialNumber = serial.serial < 0 ? 0 : serial.serial + 1;
        }
      }
    }

    for (let i = 0; i < quantidade; i++) {
      try {
        if (empresa?.filter === undefined) {
          alert.error("Configuração de filtro EPC não encontrada");
          return [];
        }
        if (empresa?.partition === undefined) {
          alert.error("Partição da empresa não encontrado");
          return [];
        }
        let sscc = empresa.valor + serialNumber.toString().padStart(6, "0");
        sscc += calculateSSCCCheckDigit(sscc.padStart(17, "0")).toString();
        let epc1 = new epcTds.Sscc96()
          .setFilter(empresa.filter)
          .setPartition(empresa.partition)
          .setCompanyPrefix(parseInt(empresa.valor))
          .setSerialReference(serialNumber);
        let epc = epc1.toHexString();
        epc = epc.toUpperCase();
        if (epc.length > 24) {
          alert.error(
            `SSCC gerado excede o tamanho máximo de 24 caracteres (${epc.length}).\n` +
              `Empresa: ${empresa.valor}${empresa.descricao ? ` - ${empresa.descricao}` : ""}\n` +
              `EPC: ${epc}`,
          );
          return [];
        }
        if (epc.length < 24) {
          epc = epc.padEnd(24, "0");
        }
        let _id = gerarIdEpc();
        let zpl = gerarZplImpressaoBo(
          {
            _id: "",
            descricao: empresa.descricao,
            ean: empresa.valor,
            inf_compl_1: "",
            id_conta: impressaoConfig?.id_conta || "",
            inf_compl_2: "",
            inf_compl_10: "",
            inf_compl_11: "",
            inf_compl_12: "",
            inf_compl_3: "",
            inf_compl_4: "",
            inf_compl_5: "",
            inf_compl_6: "",
            inf_compl_7: "",
            inf_compl_8: "",
            inf_compl_9: "",
          },
          serialNumber.toString(),
          epc,
          _id,
          numeroAtualImpressoes,
          localizacoes,
        );
        epcs.push({
          _id,
          epc: epc,
          serial: serialNumber.toString(),
          zpl_impressao_bo: zpl,
          numero_impressao: numeroAtualImpressoes,
        });
        numeroAtualImpressoes++;
        serialNumber++;
      } catch (error) {
        alert.error("Erro ao gerar SSCC");
        return [];
      }
    }
    return epcs;
  };

  //

  const gerarEpcs = (
    categoria: CategoriaItemResponse,
    quantidade: number = 1,
    localizacoes: string[] = [],
    blocks?: EPCBlock[],
    serialInicial: number = 0,
    codigoPanvel: string = "000001",
  ) => {
    if (!categoria) {
      alert.error("Categoria não fornecida");
      return [];
    }
    if (quantidade <= 0) {
      return [];
    }
    if (impressaoConfig?.gravacao_reversa) {
      return gerarEpcsGravacaoReversa(
        categoria,
        quantidade,
        serialInicial,
        localizacoes,
      );
    }
    let padrao_epc = coletarPadraoEpc();
    if (padrao_epc === "personalizado") {
      return gerarEpcsPersonalizado(
        blocks || coletarBlocosEPC(),
        categoria,
        quantidade,
        serialInicial,
        codigoPanvel,
        localizacoes,
      );
    }
    if (padrao_epc === "gs1 gtin-13") {
      return gerarEpcsGS1(categoria, quantidade, serialInicial, localizacoes);
    }
    return [];
  };

  const gerarZplImpressaoBo = (
    categoria: CategoriaItemResponse,
    serial: string,
    epc: string,
    _id: string,
    numero_de_impressao: number,
    localizacoes: string[],
    codigoPanvel: string = "000001",
  ) => {
    let zplParaEdicao = "";
    if (coletaZplPadrao() === undefined) {
      alert.error("Etiqueta padrão não definida.");
      return "";
    }
    const { zpl } = coletaZplPadrao()!;
    if (!zpl) {
      return "";
    }
    zplParaEdicao = zpl;
    if (!zplParaEdicao) {
      return "";
    }
    zplParaEdicao = zplParaEdicao.replace(/{ean}/g, categoria.ean);
    zplParaEdicao = zplParaEdicao.replace(
      /{eansemzero}/g,
      categoria.ean.replace(/^0+/, ""),
    );
    zplParaEdicao = zplParaEdicao.replace(/^0+/, "");
    if (zplParaEdicao.includes("{sscc}")) {
      zplParaEdicao = zplParaEdicao.replace(
        /{sscc}/g,
        "(00)" + epcTds.valueOf(epc).toBarcode(),
      );
    }
    if (!impressaoConfig?.gravacao_reversa) {
      zplParaEdicao = zplParaEdicao.replace(/{epc}/g, epc);
      // epc sem zero a esquerda
      zplParaEdicao = zplParaEdicao.replace(
        /{epcsze}/g,
        epc.replace(/^0+/, ""),
      );
    }
    zplParaEdicao = zplParaEdicao.replace(/{epc6final}/g, epc.slice(-6));
    zplParaEdicao = zplParaEdicao.replace(/{epc3final}/g, epc.slice(-3));
    zplParaEdicao = zplParaEdicao.replace(/{desc}/g, categoria.descricao);
    zplParaEdicao = zplParaEdicao.replace(/{serial}/g, serial);
    zplParaEdicao = zplParaEdicao.replace(
      /\{serial\+1\}/g,
      (parseInt(serial) + 1).toString(),
    );
    zplParaEdicao = zplParaEdicao.replace(
      /{serialc2ze}/g,
      serial.toString().padStart(2, "0"),
    );
    zplParaEdicao = zplParaEdicao.replace(
      /{serialc3ze}/g,
      serial.toString().padStart(3, "0"),
    );
    zplParaEdicao = zplParaEdicao.replace(
      /{serialcze}/g,
      serial.toString().padStart(4, "0"),
    );
    zplParaEdicao = zplParaEdicao.replace(
      /{serialc5ze}/g,
      serial.toString().padStart(5, "0"),
    );
    zplParaEdicao = zplParaEdicao.replace(
      /{serialc6ze}/g,
      serial.toString().padStart(6, "0"),
    );
    zplParaEdicao = zplParaEdicao.replace(
      /{serialc7ze}/g,
      serial.toString().padStart(7, "0"),
    );
    zplParaEdicao = zplParaEdicao.replace(
      /{serialc8ze}/g,
      serial.toString().padStart(8, "0"),
    );
    // Infos do tipo data/datahora ficam gravadas em ISO (AAAA-MM-DD) por conta
    // dos inputs nativos; na etiqueta devem sair como DD/MM/AAAA.
    for (let idx = 1; idx <= 12; idx++) {
      const bruto =
        (categoria[`inf_compl_${idx}` as keyof CategoriaItemResponse] as
          | string
          | undefined) ?? "";
      zplParaEdicao = zplParaEdicao.replace(
        new RegExp(`\\{info${idx}\\}`, "g"),
        formatarValorInfoCompl(getTipoInfoCompl(impressaoConfig, idx), bruto),
      );
    }
    zplParaEdicao = zplParaEdicao.replace(
      /{n_impressao}/g,
      (numero_de_impressao + 1).toString(),
    );
    zplParaEdicao = zplParaEdicao.replace(/{codigo_panvel}/g, codigoPanvel);

    zplParaEdicao = zplParaEdicao.replace(/{id_item}/g, _id);

    zplParaEdicao = zplParaEdicao.replace(
      /{data-hora}/g,
      new Date()
        .toLocaleString("pt-BR", {
          timeZone: "America/Sao_Paulo",
        })
        .replace(",", ""),
    );
    zplParaEdicao = zplParaEdicao.replace(
      /{data}/g,
      new Date()
        .toLocaleDateString("pt-BR", {
          timeZone: "America/Sao_Paulo",
        })
        .replace(",", ""),
    );

    // data2 = AAAAMMDD
    zplParaEdicao = zplParaEdicao.replace(
      /{data2}/g,
      new Date().toISOString().slice(0, 10).replace(/-/g, ""),
    );

    if (impressaoConfig) {
      let newIdContaEpc = {
        id_conta: impressaoConfig.id_conta,
        epc: epc,
      };
      zplParaEdicao = zplParaEdicao.replace(
        /{id_conta}/g,
        newIdContaEpc.id_conta,
      );
    }
    zplParaEdicao = zplParaEdicao.replace(
      /{nivel_loc_1}/g,
      localizacoes[0] || "",
    );
    zplParaEdicao = zplParaEdicao.replace(
      /{nivel_loc_2}/g,
      localizacoes[1] || "",
    );
    zplParaEdicao = zplParaEdicao.replace(
      /{nivel_loc_3}/g,
      localizacoes[2] || "",
    );
    zplParaEdicao = zplParaEdicao.replace(
      /{nivel_loc_4}/g,
      localizacoes[3] || "",
    );
    zplParaEdicao = zplParaEdicao.replace(
      /{valor_unidade}/g,
      categoria.valor_unidade != null ? String(categoria.valor_unidade) : "",
    );
    zplParaEdicao = zplParaEdicao.replace(
      /{unidade_medida}/g,
      categoria.unidade_medida ?? "",
    );
    return zplParaEdicao;
  };

  const gerarEpcsGravacaoReversa = (
    categoria: CategoriaItemResponse,
    quantidade: number = 1,
    serialInicial: number = 0,
    localizacoes: string[] = [],
  ) => {
    if (!categoria) {
      return [];
    }
    const epcs: EpcItemGerado[] = [];
    let serialNumber = serialInicial;
    let numeroAtualImpressoes = numeroDeImpressoes || 0;
    if (seriais.length && serialInicial == 0) {
      const serial = seriais.find((s) => s.gtin === categoria.ean);
      if (serial) {
        serialNumber = serial.serial < 0 ? 0 : serial.serial + 1;
      }
    }
    for (let i = 0; i < quantidade; i++) {
      let _id = gerarIdEpc();
      epcs.push({
        _id,
        epc: `Aguardando EPC ${serialNumber}`,
        serial: serialNumber.toString(),
        zpl_impressao_bo: gerarZplImpressaoBo(
          categoria,
          serialNumber.toString(),
          `Aguardando EPC ${serialNumber}`,
          _id,
          numeroAtualImpressoes,
          localizacoes,
        ),
        numero_impressao: numeroAtualImpressoes,
      });
      numeroAtualImpressoes++;
      serialNumber++;
    }
    return epcs;
  };
  const gerarEpcsGS1 = (
    categoria: CategoriaItemResponse,
    quantidade: number = 1,
    serialInicial: number = 0,
    localizacoes: string[] = [],
  ) => {
    if (!categoria) {
      return [];
    }
    const epcs: EpcItemGerado[] = [];
    let serialNumber = serialInicial;
    let numeroAtualImpressoes = numeroDeImpressoes || 0;

    if (seriais.length && serialInicial == 0) {
      const serial = seriais.find((s) => s.gtin === categoria.ean);
      if (serial) {
        serialNumber = serial.serial < 0 ? 0 : serial.serial + 1;
      }
    }

    for (let i = 0; i < quantidade; i++) {
      let epc = "";
      try {
        if (!impressaoConfig?.filter_epc) {
          alert.error("Configuração de filtro EPC não encontrada");
          return [];
        }
        if (!impressaoConfig?.company_prefix) {
          alert.error("Prefixo da empresa não encontrado");
          return [];
        }
        let filter = JSON.parse(impressaoConfig?.filter_epc);
        let companyPrefix = JSON.parse(impressaoConfig?.company_prefix);
        let epc_gerado = new epcTds.Sgtin96()
          .setFilter(parseInt(filter.valor))
          .setPartition(parseInt(companyPrefix.valor))
          .setGtin(categoria.ean)
          .setSerial(serialNumber);
        epc = epc_gerado.toHexString();
      } catch (error) {
        alert.error("Erro ao gerar EPC GS1");
        return [];
      }
      epc.toUpperCase();
      let _id = gerarIdEpc();

      epcs.push({
        _id,
        epc: epc,
        serial: serialNumber.toString(),
        zpl_impressao_bo: gerarZplImpressaoBo(
          categoria,
          serialNumber.toString(),
          epc,
          _id,
          numeroAtualImpressoes,
          localizacoes,
        ),
        numero_impressao: numeroDeImpressoes,
      });
      numeroAtualImpressoes++;
      serialNumber++;
    }
    return epcs;
  };

  const gerarEpcsPersonalizado = (
    blocks: EPCBlock[],
    categoria: CategoriaItemResponse | undefined,
    quantidade: number = 1,
    serialInicial: number = 0,
    codigoPanvel: string = "000001",
    localizacoes: string[] = [],
  ) => {
    try {
      if (!categoria) {
        return [];
      }
      if (blocks.length === 0) {
        alert.error("EPC personalizado não configurado");
        return [];
      }

      const epcs: EpcItemGerado[] = [];
      let serialNumber = serialInicial;
      let numeroAtualImpressoes = numeroDeImpressoes || 0;
      if (seriais.length && serialInicial == 0) {
        const serial = seriais.find((s) => s.gtin === categoria.ean);
        if (serial) {
          serialNumber = serial.serial < 0 ? 0 : serial.serial + 1;
        }
      }

      for (let i = 0; i < quantidade; i++) {
        // Data/hora gerada para cada EPC
        const currentDate = new Date();
        const day = String(currentDate.getDate()).padStart(2, "0");
        const month = String(currentDate.getMonth() + 1).padStart(2, "0");
        const year = String(currentDate.getFullYear()).slice(-2);
        const hour = String(currentDate.getHours()).padStart(2, "0");
        const minute = String(currentDate.getMinutes()).padStart(2, "0");
        const second = String(currentDate.getSeconds()).padStart(2, "0");
        const daymonthhex = parseInt(`${day}${month}`, 10)
          .toString(16)
          .toUpperCase();
        const hourminutessecondshex = parseInt(`${hour}${minute}${second}`, 10)
          .toString(16)
          .toUpperCase();

        let epc = blocks
          .map((block) => {
            switch (block.id) {
              case "{info1}":
                return categoria?.inf_compl_1 || "";
              case "{info2}":
                return categoria?.inf_compl_2 || "";
              case "{info3}":
                return categoria?.inf_compl_3 || "";
              case "{info4}":
                return categoria?.inf_compl_4 || "";
              case "{info5}":
                return categoria?.inf_compl_5 || "";
              case "{info6}":
                return categoria?.inf_compl_6 || "";
              case "{info7}":
                return categoria?.inf_compl_7 || "";
              case "{info8}":
                return categoria?.inf_compl_8 || "";
              case "{info9}":
                return categoria?.inf_compl_9 || "";
              case "{info10}":
                return categoria?.inf_compl_10 || "";
              case "{info2sze}":
                return "INFO2SZE";

              // Códigos e Seriais
              case "{codigo}":
                return categoria?.ean || "";
              case "{codigosze}":
                return categoria?.ean.replace(/^0+/, "") || "";
              case "{serial}":
                return serialNumber;
              case "{serialhex}":
                return serialNumber.toString(16).toUpperCase();
              case "{serial+1}":
                return (serialNumber + 1).toString();
              case "{serial+1cze}":
                return (serialNumber + 1).toString().padStart(2, "0");
              case "{serialc2ze}":
                return serialNumber.toString().padStart(2, "0");
              case "{serialc3ze}":
                return serialNumber.toString().padStart(3, "0");
              case "{serialcze}":
                return serialNumber.toString().padStart(4, "0");
              case "{serialc5ze}":
                return serialNumber.toString().padStart(5, "0");
              case "{serialc6ze}":
                return serialNumber.toString().padStart(6, "0");
              case "{serialc7ze}":
                return serialNumber.toString().padStart(7, "0");
              case "{serialc8ze}":
                return serialNumber.toString().padStart(8, "0");
              case "{codigo-panvel}":
                return codigoPanvel;

              case "{n_impressao}":
                return numeroAtualImpressoes.toString();

              // Tempo (gerado por EPC)
              case "{dd}":
                return day;
              case "{mm}":
                return month;
              case "{aa}":
                return year;
              case "{aaaa}":
                return String(currentDate.getFullYear());
              case "{hh}":
                return hour;
              case "{mn}":
                return minute;
              case "{ss}":
                return second;
              case "{dd_mm_hex}":
                return daymonthhex.padStart(3, "0");
              case "{hh_mn_ss_hex}":
                return hourminutessecondshex.padStart(5, "0");

              // Descrição
              case "{desc}":
                return categoria?.descricao || "";

              // Caracteres e números
              case "{A}":
                return "A";
              case "{B}":
                return "B";
              case "{C}":
                return "C";
              case "{D}":
                return "D";
              case "{E}":
                return "E";
              case "{f}":
                return "F";
              case "{0}":
                return "0";
              case "{1}":
                return "1";
              case "{2}":
                return "2";
              case "{3}":
                return "3";
              case "{4}":
                return "4";
              case "{5}":
                return "5";
              case "{6}":
                return "6";
              case "{7}":
                return "7";
              case "{8}":
                return "8";
              case "{9}":
                return "9";
              case "{<<>>}":
                return "<<>>"; // Implementar logica de espaçamento
              // Campo personalizado
            }
            if (block.id.includes("personalizado-")) {
              return block.id
                .replace(`${block.id.split("-")[0]}-`, "")
                .replace("}", "");
            }
          })
          .join("");

        let epcEspacado = epc.split("<<>>");
        epc = epcEspacado[0];
        if (epcEspacado.length > 1) {
          epc = epc.padEnd(24 - epcEspacado[1].length, "0");
          epc += epcEspacado[1];
        }
        epc = epc.padStart(24, "0");
        epc = epc.toUpperCase();

        if (epc.length > 24) {
          alert.error(
            `EPC gerado excede o tamanho máximo de 24 caracteres (${epc.length}).\n` +
              `Produto: ${categoria.ean}${categoria.descricao ? ` - ${categoria.descricao}` : ""}\n` +
              `EPC: ${epc}`,
          );
          return [];
        }
        if (epc.length < 24) {
          epc = epc.padEnd(24, "0");
        }
        let _id = gerarIdEpc();
        epcs.push({
          _id,
          epc: epc,
          serial: serialNumber.toString(),
          zpl_impressao_bo: gerarZplImpressaoBo(
            categoria,
            serialNumber.toString(),
            epc,
            _id,
            numeroAtualImpressoes,
            localizacoes,
            codigoPanvel,
          ),
          numero_impressao: numeroAtualImpressoes,
        });
        numeroAtualImpressoes++;
        serialNumber++;
      }
      return epcs;
    } catch (error) {
      alert.error("Erro ao gerar EPC personalizado");
      return [];
    }
  };

  return (
    <EpcContext.Provider
      value={{
        gerarEpcs,
        gerarSSCC,
      }}
    >
      {children}
    </EpcContext.Provider>
  );
}

export function useEpc() {
  const context = useContext(EpcContext);
  if (!context) {
    throw new Error("useEpc deve ser usado dentro de um EpcProvider");
  }
  return context;
}
